import React, { useState } from 'react';
import { useUsers } from '../context/UsersContext';
import { C } from '../utils/styles';
import Icon from './Icon';

export default function ChangePasswordModal({ onClose }) {
  const { currentUser, changePassword } = useUsers();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const validatePassword = (pwd) => {
    if (pwd.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(pwd)) return 'Password must contain an uppercase letter';
    if (!/[0-9]/.test(pwd)) return 'Password must contain a number';
    if (!/[!@#$%^&*]/.test(pwd)) return 'Password must contain a symbol (!@#$%^&*)';
    return null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // Validate new password
    const validationError = validatePassword(newPassword);
    if (validationError) {
      setError(validationError);
      return;
    }

    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    // Check if new password is different from current
    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    // Attempt to change password
    const result = changePassword(currentUser.id, currentPassword, newPassword);
    
    if (!result) {
      setError('Current password is incorrect');
      return;
    }

    // Success
    setSuccess(true);
    setTimeout(() => {
      onClose();
    }, 1500);
  };

  if (success) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: 20,
        }}
      >
        <div
          style={{
            background: 'white',
            borderRadius: 12,
            padding: 32,
            maxWidth: 400,
            width: '100%',
            textAlign: 'center',
          }}
        >
          <div style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: C.greenLight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <Icon name="checkmark-outline" style={{ fontSize: 32, color: C.green }} />
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0, marginBottom: 8 }}>
            Password Changed!
          </h2>
          <p style={{ fontSize: 13, color: C.textMid, margin: 0 }}>
            Your password has been updated successfully.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 12,
          padding: 28,
          maxWidth: 450,
          width: '100%',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0, marginBottom: 20 }}>
          Change Password
        </h2>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label htmlFor="current-password-modal" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>
              Current Password *
            </label>
            <input
              id="current-password-modal"
              name="current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              style={{
                width: '100%',
                padding: '10px 14px',
                fontSize: 13,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                background: C.bg,
                color: C.text,
              }}
              required
            />
          </div>

          <div style={{ borderTop: `1px solid ${C.border}`, margin: '8px 0' }} />

          <div>
            <label htmlFor="new-password-modal" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>
              New Password *
            </label>
            <input
              id="new-password-modal"
              name="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              style={{
                width: '100%',
                padding: '10px 14px',
                fontSize: 13,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                background: C.bg,
                color: C.text,
              }}
              required
            />
          </div>

          <div>
            <label htmlFor="confirm-password-modal" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>
              Confirm New Password *
            </label>
            <input
              id="confirm-password-modal"
              name="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              style={{
                width: '100%',
                padding: '10px 14px',
                fontSize: 13,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                background: C.bg,
                color: C.text,
              }}
              required
            />
          </div>

          <div style={{
            background: C.blueLight,
            border: `1px solid ${C.blue}`,
            borderRadius: 6,
            padding: 12,
            fontSize: 11,
            color: C.textMid,
          }}>
            Password must be at least 8 characters with one uppercase, one number, and one symbol.
          </div>

          {error && (
            <div style={{
              background: C.redLight,
              border: `1px solid ${C.red}`,
              borderRadius: 6,
              padding: 10,
              fontSize: 12,
              color: C.red,
              fontWeight: 600,
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '10px',
                background: 'transparent',
                color: C.textMid,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                flex: 1,
                padding: '10px',
                background: C.accent,
                color: 'white',
                border: 'none',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <Icon name="lock-closed-outline" style={{ fontSize: 14 }} />
              Change Password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
